import { useId } from "react";

import { LOGO, LOGO_CUTS } from "./logoGeometry";

const PATHS = {
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  call: (
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
  ),
  pin: (
    <>
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a1 1 0 0 1 1-1h10" />
    </>
  ),
  check: <path d="M5 12l5 5L20 7" />,
  chat: <path d="M21 12a9 9 0 0 1-13.5 7.8L3 21l1.2-4.5A9 9 0 1 1 21 12z" />,
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  phone: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  signal: (
    <>
      <path d="M2 8.5a15 15 0 0 1 20 0M5 12a10 10 0 0 1 14 0M8.5 15.5a5 5 0 0 1 7 0" />
      <circle cx="12" cy="19" r="1" />
    </>
  ),
  tv: (
    <>
      <rect x="2" y="5" width="20" height="13" rx="2" />
      <path d="M8 22h8M12 18v4" />
    </>
  ),
  bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  card: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </>
  ),
  refund: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </>
  ),
  ledger: <path d="M4 4h16v16H4zM4 10h16M10 4v16" />,
  shield: <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />,
  chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  gift: (
    <>
      <rect x="3" y="8" width="18" height="5" rx="1" />
      <path d="M5 13v8h14v-8M12 8v13M12 8S10.5 3 8 3.5 6.5 8 12 8zM12 8s1.5-5 4-4.5S17.5 8 12 8z" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4M8 15l2.5 2.5L16 13" />
    </>
  ),
  handshake: (
    <path d="M2 11l4-4 4 2 3-2 4 1 5 3M2 11l5 5M7 16l2-1 2 2 2-1 2 2 4-4-4-4M13 9l-3 3a1.5 1.5 0 0 0 2 2l3-2" />
  ),
  form: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18.5 14a6.5 6.5 0 0 1 3 6" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
} as const;

export type IconName = keyof typeof PATHS;

type Props = {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
};

export function Icon({
  name,
  size = 20,
  color = "currentColor",
  strokeWidth = 2,
}: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

// The Hethera mark: the H with a message bubble across its right leg and
// "* * *" (a PIN) inside it (brand/README.md). Drawn on the logo's 512 grid,
// so it carries its own padding: give it the size of the tile it sits on.
// The gap around the bubble and the asterisks are real holes, so it works on
// any background. At 48 px and below it uses the small cut.
// `crop` trims the padding to the mark's own bounds (the H's height, the
// bubble's reach), for using it as the H of the "Hethera" wordmark; `size` is
// then its height. `className` lets CSS size it instead (e.g. in em).
const CROP = { x: 108, y: 98, width: 362, height: 316 };

export function LogoMark({
  size = 34,
  color = "#c6f160",
  crop = false,
  className,
}: {
  size?: number;
  color?: string;
  crop?: boolean;
  className?: string;
}) {
  const id = useId().replace(/:/g, "");
  const cut = LOGO_CUTS[size <= 48 ? "small" : "full"];
  const { stemW, left, right, top, bottom, barY, barH, cx, cy, angle, dx } = LOGO;
  const turn = `translate(${cx} ${cy}) rotate(${angle})`;
  const stars = cut.stars.map(([x, y]) =>
    [0, 60, 120].map((a) => (
      <rect
        key={`${x}-${a}`}
        x={x - cut.starT / 2}
        y={y - cut.starR}
        width={cut.starT}
        height={cut.starR * 2}
        rx={cut.starT / 2}
        transform={`rotate(${a} ${x} ${y})`}
        fill="#000"
      />
    )),
  );
  return (
    <svg
      className={className}
      width={crop ? (size * CROP.width) / CROP.height : size}
      height={size}
      viewBox={crop ? `${CROP.x} ${CROP.y} ${CROP.width} ${CROP.height}` : "0 0 512 512"}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <mask id={`${id}h`} maskUnits="userSpaceOnUse" x="0" y="0" width="512" height="512">
          <rect width="512" height="512" fill="#fff" />
          <path
            transform={turn}
            d={cut.bubble}
            fill="#000"
            stroke="#000"
            strokeWidth={cut.gap * 2}
            strokeLinejoin="round"
          />
        </mask>
        <mask id={`${id}b`} maskUnits="userSpaceOnUse" x="-400" y="-400" width="800" height="800">
          <rect x="-400" y="-400" width="800" height="800" fill="#fff" />
          {stars}
        </mask>
      </defs>
      <g transform={`translate(${dx} 0)`} fill={color}>
        <g mask={`url(#${id}h)`}>
          <rect x={left} y={top} width={stemW} height={bottom - top} rx={stemW / 2} />
          <rect x={right} y={top} width={stemW} height={bottom - top} rx={stemW / 2} />
          <rect x={left + 40} y={barY} width={right - left} height={barH} rx={barH / 2} />
        </g>
        <g transform={turn}>
          <path d={cut.bubble} mask={`url(#${id}b)`} />
        </g>
      </g>
    </svg>
  );
}
